import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Button } from './button';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';
import { DateRangeFilter } from './date-range-filter';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';
import { useGlobalDateRange, useGlobalDateNavigation, useEntityDataForDateFilter } from '@/store/date-filter.store';
import { useNavigationStore } from '@/store/navigation.store';

interface GlobalDateFilterProps {
  className?: string;
  buttonSize?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

export function GlobalDateFilter({
  className,
  buttonSize = 'sm',
  isLoading = false,
}: GlobalDateFilterProps) {
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  
  // Global date range state
  const { dateRange, setDateRange } = useGlobalDateRange();
  
  // Navigation state
  const { 
    navigateNext, 
    navigatePrevious, 
    canNavigateNext, 
    canNavigatePrevious, 
    isNavigating,
    navigationStrategy,
    getNextRangePreview,
    getPreviousRangePreview
  } = useGlobalDateNavigation();
  
  // Get current entity for Financial Year calculations
  const selectedEntityId = useNavigationStore((state) => state.selectedEntityId);
  const entityId = !selectedEntityId || selectedEntityId === 'all' ? null : selectedEntityId;
  const { entityData, isLoading: entityLoading } = useEntityDataForDateFilter(entityId);

  // Track if initialization has been attempted to prevent repeated attempts
  const initializationAttempted = useRef(false);

  // Helper function to calculate current financial year based on entity data
  const calculateCurrentFY = useCallback(() => {
    if (!entityData?.financial_year_end_month || !entityData?.financial_year_end_day) {
      // Fallback to calendar year if financial year data is not available
      const year = new Date().getFullYear();
      return {
        start: `${year}-01-01`,
        end: `${year}-12-31`
      };
    }

    const endMonth = entityData.financial_year_end_month;
    const endDay = entityData.financial_year_end_day;

    // Validate financial year data
    if (endMonth < 1 || endMonth > 12 || endDay < 1 || endDay > 31) {
      const year = new Date().getFullYear();
      return {
        start: `${year}-01-01`,
        end: `${year}-12-31`
      };
    }

    try {
      // Use UTC methods to avoid timezone issues
      const today = new Date();
      const currentYear = today.getUTCFullYear();

      // Determine which financial year we're currently in (using UTC)
      const currentYearEnd = new Date(Date.UTC(currentYear, endMonth - 1, endDay));
      const isAfterYearEnd = today > currentYearEnd;

      // Current FY: if we're after the FY end, we're in the next FY
      const fyEndYear = isAfterYearEnd ? currentYear + 1 : currentYear;

      // Calculate FY end date using UTC
      const fyEnd = new Date(Date.UTC(fyEndYear, endMonth - 1, endDay));

      // Calculate FY start date (day after previous year's end) using UTC
      const fyStart = new Date(Date.UTC(fyEndYear - 1, endMonth - 1, endDay));
      fyStart.setUTCDate(fyStart.getUTCDate() + 1);

      return {
        start: fyStart.toISOString().split('T')[0],
        end: fyEnd.toISOString().split('T')[0]
      };
    } catch {
      // Return calendar year as fallback
      const year = new Date().getFullYear();
      return {
        start: `${year}-01-01`,
        end: `${year}-12-31`
      };
    }
  }, [entityData]);

  // Initialize with current FY if no date range is set and entity data is loaded
  useEffect(() => {
    // Only initialize once and only if:
    // 1. No date range is currently set (null or completely empty)
    // 2. Entity data is available (or we can use calendar year fallback)
    // 3. Not currently loading entity data
    // 4. Haven't already attempted initialization
    if (!initializationAttempted.current &&
        !entityLoading &&
        (!dateRange || (!dateRange.start && !dateRange.end))) {

      initializationAttempted.current = true;

      const currentFY = calculateCurrentFY();

      setDateRange(currentFY);
    }
  }, [dateRange, entityLoading, entityData, entityId, calculateCurrentFY, setDateRange]);

  // Consolidated date formatting function
  const formatDateForDisplay = useCallback((dateStr: string, options: { includeYear?: boolean; fullYear?: boolean } = {}) => {
    const { includeYear = true, fullYear = false } = options;
    const date = new Date(dateStr + 'T00:00:00.000Z');
    const currentYear = new Date().getUTCFullYear();
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: includeYear ? (fullYear || date.getUTCFullYear() !== currentYear ? 'numeric' : undefined) : undefined,
      timeZone: 'UTC'
    });
  }, []);

  // Helper function to format a range for tooltip display
  const formatRangeForTooltip = useCallback((range: { start: string; end: string }) => {
    const startDate = new Date(range.start + 'T00:00:00.000Z');
    const endDate = new Date(range.end + 'T00:00:00.000Z');
    
    // If both dates are in the same year, only show year on the end date
    const sameYear = startDate.getUTCFullYear() === endDate.getUTCFullYear();
    
    if (sameYear) {
      return `${formatDateForDisplay(range.start, { includeYear: false })} - ${formatDateForDisplay(range.end, { fullYear: true })}`;
    } else {
      return `${formatDateForDisplay(range.start, { fullYear: true })} - ${formatDateForDisplay(range.end, { fullYear: true })}`;
    }
  }, [formatDateForDisplay]);

  // Format date range for display
  const formatDateRange = useCallback(() => {
    if (!dateRange || (!dateRange.start && !dateRange.end)) {
      return 'Select period';
    }
    
    if (dateRange.start && dateRange.end) {
      // Use the same smart year logic as tooltips
      return formatRangeForTooltip({ start: dateRange.start, end: dateRange.end });
    } else if (dateRange.start) {
      return `From ${formatDateForDisplay(dateRange.start)}`;
    } else if (dateRange.end) {
      return `To ${formatDateForDisplay(dateRange.end)}`;
    }
    
    return 'Select period';
  }, [dateRange, formatRangeForTooltip, formatDateForDisplay]);
  
  const hasActiveDateFilter = dateRange && (dateRange.start || dateRange.end);
  
  // Get current full range tooltip - memoized for performance
  const getCurrentRangeTooltip = useMemo(() => {
    if (!dateRange || (!dateRange.start && !dateRange.end)) {
      return 'No date filter active';
    }
    
    if (dateRange.start && dateRange.end) {
      return `Current range: ${formatRangeForTooltip({ start: dateRange.start, end: dateRange.end })}`;
    } else if (dateRange.start) {
      return `From ${formatDateForDisplay(dateRange.start, { fullYear: true })}`;
    } else if (dateRange.end) {
      return `To ${formatDateForDisplay(dateRange.end, { fullYear: true })}`;
    }
    
    return 'No date filter active';
  }, [dateRange, formatRangeForTooltip, formatDateForDisplay]);
  
  // Safe tooltip content generator with error boundaries
  const getSafeTooltipContent = useCallback((getter: () => string, fallback: string): string => {
    try {
      return getter();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('Tooltip content calculation failed:', error);
      }
      return fallback;
    }
  }, []);

  // Get navigation tooltips - memoized for performance with error boundaries
  const getNextRangeTooltip = useMemo(() => {
    return getSafeTooltipContent(() => {
      const preview = getNextRangePreview();
      if (!preview) return 'No next range available';
      return `Next: ${formatRangeForTooltip(preview)}`;
    }, 'Next navigation unavailable');
  }, [getNextRangePreview, formatRangeForTooltip, getSafeTooltipContent]);

  const getPreviousRangeTooltip = useMemo(() => {
    return getSafeTooltipContent(() => {
      const preview = getPreviousRangePreview();
      if (!preview) return 'No previous range available';
      return `Previous: ${formatRangeForTooltip(preview)}`;
    }, 'Previous navigation unavailable');
  }, [getPreviousRangePreview, formatRangeForTooltip, getSafeTooltipContent]);
  
  // Keyboard navigation handler (memoized for performance)
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && datePopoverOpen) {
      setDatePopoverOpen(false);
      return;
    }
    
    // Handle arrow key navigation when component is focused
    if (e.key === 'ArrowLeft' && canNavigatePrevious) {
      e.preventDefault();
      e.stopPropagation();
      navigatePrevious();
    } else if (e.key === 'ArrowRight' && canNavigateNext) {
      e.preventDefault();
      e.stopPropagation();
      navigateNext();
    }
  }, [datePopoverOpen, canNavigatePrevious, canNavigateNext, navigatePrevious, navigateNext]);
  
  // Map buttonSize to valid Button component sizes
  const mappedButtonSize = buttonSize === 'md' ? 'default' : buttonSize;
  
  return (
    <TooltipProvider>
      <div className={cn("flex items-center gap-1", className)}>
        {/* Previous Month Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size={mappedButtonSize}
              onClick={navigatePrevious}
              disabled={!canNavigatePrevious || isLoading || entityLoading || isNavigating}
              className="h-8 w-8 p-0 bg-white text-xs border border-input hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
              aria-label={`Previous ${navigationStrategy === 'monthly' ? 'month' : 'period'}`}
              aria-describedby="previous-range-tooltip"
            >
              <ChevronLeft className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent id="previous-range-tooltip">
            <p>{getPreviousRangeTooltip}</p>
          </TooltipContent>
        </Tooltip>

        {/* Main Date Filter Button */}
        <Tooltip>
          <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
            <PopoverTrigger asChild>
              <TooltipTrigger asChild>
                <Button
                  variant={hasActiveDateFilter ? "default" : "outline"}
                  className={cn(
                    "justify-start gap-2 bg-white text-xs h-8 flex-1 min-w-0",
                    hasActiveDateFilter && "bg-blue-600 hover:bg-blue-700 text-white"
                  )}
                  disabled={isLoading || entityLoading}
                  aria-label={hasActiveDateFilter ? `Date filter active: ${formatDateRange()}` : "Open date filter"}
                  aria-expanded={datePopoverOpen}
                  aria-haspopup="dialog"
                  aria-describedby="current-range-tooltip"
                  onKeyDown={handleKeyDown}
                >
                  <Calendar className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{formatDateRange()}</span>
                </Button>
              </TooltipTrigger>
            </PopoverTrigger>
        <PopoverContent className="w-96 p-0" align="start">
          <div className="p-4 border-b">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Global Transaction Date Filter
            </h4>
            <p className="text-xs text-gray-500 mt-1">
              This filter applies to all transaction pages: Dashboard, Compliance, Bills Amortization, and Accpay
            </p>
          </div>
          <div className="p-4">
            {/* Show loading state during initialization to prevent flash of empty required fields */}
            {(!dateRange || (!dateRange.start && !dateRange.end)) && entityLoading ? (
              <div className="flex items-center justify-center p-4">
                <div className="text-xs text-gray-500">Loading Financial Year...</div>
              </div>
            ) : (
              <DateRangeFilter
                value={(dateRange && dateRange.start && dateRange.end) ? { start: dateRange.start, end: dateRange.end } : calculateCurrentFY()}
                onChange={(newRange) => {
                  // Validate that both dates are provided (required fields)
                  if (!newRange?.start || !newRange?.end) {
                    console.error('[GlobalDateFilter] Invalid date range received:', newRange);
                    return;
                  }

                  setDateRange(newRange);
                  // Close popover when both dates are selected
                  setDatePopoverOpen(false);
                }}
                isLoading={isLoading || entityLoading}
                entityData={entityData}
              />
            )}
          </div>
        </PopoverContent>
          </Popover>
          <TooltipContent id="current-range-tooltip">
            <p>{getCurrentRangeTooltip}</p>
          </TooltipContent>
        </Tooltip>

        {/* Next Month Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size={mappedButtonSize}
              onClick={navigateNext}
              disabled={!canNavigateNext || isLoading || entityLoading || isNavigating}
              className="h-8 w-8 p-0 bg-white text-xs border border-input hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
              aria-label={`Next ${navigationStrategy === 'monthly' ? 'month' : 'period'}`}
              aria-describedby="next-range-tooltip"
            >
              <ChevronRight className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent id="next-range-tooltip">
            <p>{getNextRangeTooltip}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}